import type { AgentPatch } from '@tunasong/agent-tools'
import { graphHooks } from '@tunasong/graph-lib/react'
import { Edit as ProposalIcon } from '@tunasong/icons'
import { logger } from '@tunasong/models'
import type { TunaEditor } from '@tunasong/plugin-lib'
import type { Agent, Comment, Entity, Persisted } from '@tunasong/schemas'
import { Button, IconButton, Menu, MenuItem } from '@tunasong/ui-lib'
import type { FC, MouseEvent } from 'react'
import { useMemo, useState } from 'react'
import { applyProposal } from './proposal-apply.js'

export interface ProposalListProps {
  agent: Persisted<Agent>
  /** Editor for the target entity, when mounted. Required to apply slate-ops proposals. */
  editor?: TunaEditor | null
  /** Current user id, stamped onto the resolution. */
  userId?: string
  className?: string
}

interface ProposalResolution {
  status: 'accepted' | 'rejected'
  at: string
  by: string
}

interface ProposalComment extends Persisted<Comment> {
  metadata?: {
    [key: string]: unknown
    agentProposal?: boolean
    targetEntityId?: string
    summary?: string
    patch?: AgentPatch
    resolution?: ProposalResolution
  }
}

const isProposal = (c: Persisted<Comment>): c is ProposalComment =>
  typeof c.metadata === 'object' &&
  c.metadata !== null &&
  (c.metadata as { agentProposal?: boolean }).agentProposal === true

const isPending = (p: ProposalComment) => !p.metadata?.resolution

export const ProposalList: FC<ProposalListProps> = ({ agent, editor, userId, className }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const { entities: children } = graphHooks.useEntityChildren<Persisted<Comment>>({
    parentId: agent.id,
  })
  const update = graphHooks.useEntityUpdate<Persisted<Comment>>({})
  const updateEntity = graphHooks.useEntityUpdate<Persisted<Entity>>({})

  const proposals = useMemo(() => children.filter(isProposal), [children])
  const pending = useMemo(() => proposals.filter(isPending), [proposals])

  const handleOpen = (ev: MouseEvent<HTMLElement>) => setAnchor(ev.currentTarget)
  const handleClose = () => setAnchor(null)

  const stampResolution = (proposal: ProposalComment, status: 'accepted' | 'rejected') => {
    const resolution: ProposalResolution = {
      status,
      at: new Date().toISOString(),
      by: userId ?? 'unknown',
    }
    void update(proposal.id, { metadata: { ...proposal.metadata, resolution } })
  }

  const handleAccept = (proposal: ProposalComment) => () => {
    const { patch, targetEntityId } = proposal.metadata ?? {}
    if (!patch || !targetEntityId) {
      logger.warn('proposal-list: accept on proposal without patch or targetEntityId', proposal.id)
      return
    }
    try {
      applyProposal(
        { targetEntityId, patch },
        {
          editor,
          updateEntity: (id, partial) => {
            void updateEntity(id, partial as Partial<Persisted<Entity>>)
          },
        }
      )
      stampResolution(proposal, 'accepted')
    } catch (error) {
      logger.warn('proposal-list: apply failed', error)
    }
  }

  const handleReject = (proposal: ProposalComment) => () => {
    stampResolution(proposal, 'rejected')
  }

  if (pending.length === 0) {
    return null
  }

  return (
    <div className={className}>
      <IconButton aria-label={`Agent proposals (${pending.length} pending)`} onClick={handleOpen} size="small">
        <ProposalIcon />
        {pending.length > 0 ? <span>{pending.length}</span> : null}
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={handleClose}>
        {pending.map(proposal => (
          <MenuItem key={proposal.id}>
            <div>
              <strong>{proposal.metadata?.summary ?? 'Proposed edit'}</strong>
              <ProposalPreview patch={proposal.metadata?.patch} />
              <div>
                <Button size="small" onClick={handleAccept(proposal)}>
                  Accept
                </Button>
                <Button size="small" onClick={handleReject(proposal)}>
                  Reject
                </Button>
              </div>
            </div>
          </MenuItem>
        ))}
      </Menu>
    </div>
  )
}

const ProposalPreview: FC<{ patch?: AgentPatch }> = ({ patch }) => {
  if (!patch) {
    return null
  }
  if (patch.kind === 'slate-ops') {
    return <small>{`${patch.ops.length} editor operation${patch.ops.length === 1 ? '' : 's'}`}</small>
  }
  const fieldNames = Object.keys(patch.fields).join(', ')
  return <small>{`update ${fieldNames}`}</small>
}
